import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft, Smartphone, CheckCircle2, Bell } from "lucide-react";
import { useT } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/app/LanguageSwitcher";
import { Footer } from "../components/app/Footer";

const Section = ({ title, children, testid }) => (
  <section className="mt-8" data-testid={testid}>
    <h2 className="font-display text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">
      {title}
    </h2>
    <div className="mt-3 text-gray-600 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function SmsOptIn() {
  const { t } = useT();
  return (
    <div className="min-h-screen flex flex-col bg-[#f9f9f8]" data-testid="sms-opt-in-page">
      <header className="border-b border-gray-200 bg-white/70 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="sms-opt-in-logo-link">
            <span className="w-8 h-8 rounded-lg bg-green-800 text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <span className="font-display font-bold text-lg tracking-tight">Tasklio AI</span>
          </Link>
          <LanguageSwitcher testidPrefix="sms-opt-in-lang-switch" />
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-800 text-xs font-medium uppercase tracking-wider">
            <Smartphone className="w-3.5 h-3.5" />
            <span>{t("smsOptIn.subtitle")}</span>
          </div>
          <h1
            className="font-display mt-4 text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight"
            data-testid="sms-opt-in-title"
          >
            {t("smsOptIn.pageTitle")}
          </h1>
          <p className="mt-4 text-gray-600 leading-relaxed" data-testid="sms-opt-in-intro">
            {t("smsOptIn.intro")}
          </p>

          {/* SMS Program Summary — top-of-page authoritative recap for
              Twilio A2P 10DLC reviewers. Single bulleted block restating
              every key compliance fact (single program, owner-only,
              customers excluded, voluntary opt-in via Settings) so the
              campaign reviewer doesn't have to scroll. */}
          <section className="mt-8" data-testid="sms-opt-in-program-summary">
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">
              {t("smsOptIn.programSummaryTitle")}
            </h2>
            <ul className="mt-3 space-y-2.5 text-gray-600 leading-relaxed">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <li
                  key={n}
                  className="flex items-start gap-2.5"
                  data-testid={`sms-opt-in-program-summary-${n}`}
                >
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-green-700 shrink-0" />
                  <span>{t(`smsOptIn.programSummary${n}`)}</span>
                </li>
              ))}
            </ul>
          </section>

          <Section title={t("smsOptIn.whoTitle")} testid="sms-opt-in-who">
            <p>{t("smsOptIn.whoBody")}</p>
          </Section>

          <Section title={t("smsOptIn.howOptInTitle")} testid="sms-opt-in-how">
            <p>{t("smsOptIn.howOptInBody")}</p>
          </Section>

          {/* "Where does opt-in happen?" — informational illustration only.
              Renders a non-interactive mockup of the Tasklio Settings card
              where business owners actually opt in. The input field has
              `readOnly` + `tabIndex={-1}` so screen readers and keyboard
              users immediately know this isn't a real form. Mirrors the
              real Settings page visual language (label, helper copy,
              bullet list) so a Twilio reviewer can verify the live
              opt-in surface from the public page. */}
          <section className="mt-8" data-testid="sms-opt-in-where">
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">
              {t("smsOptIn.whereOptInTitle")}
            </h2>
            <p className="mt-3 text-gray-600 leading-relaxed">
              {t("smsOptIn.whereOptInBody")}
            </p>

            <figure
              className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
              data-testid="sms-opt-in-settings-mockup"
              aria-label={t("smsOptIn.mockupCaption")}
            >
              {/* Faux browser/breadcrumb chrome to telegraph "this is a UI
                  preview", not a live form. */}
              <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-md bg-green-800 text-white flex items-center justify-center shrink-0">
                    <Bell className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">
                    {t("smsOptIn.mockupBreadcrumb")}
                  </span>
                </div>
                <div className="hidden sm:flex items-center gap-1.5" aria-hidden="true">
                  <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                  <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                  <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                </div>
              </div>

              <div className="p-5 sm:p-6">
                <label
                  htmlFor="sms-mockup-phone"
                  className="block text-sm font-semibold text-gray-900"
                >
                  {t("smsOptIn.mockupFieldLabel")}
                </label>
                <input
                  id="sms-mockup-phone"
                  type="text"
                  readOnly
                  tabIndex={-1}
                  aria-readonly="true"
                  value={t("smsOptIn.mockupFieldValue")}
                  className="mt-1.5 w-full h-11 px-3 rounded-md border border-gray-300 bg-gray-50 text-gray-900 text-base font-medium cursor-default focus:outline-none"
                  data-testid="sms-opt-in-mockup-input"
                />

                <p
                  className="mt-3 text-[13px] leading-relaxed text-gray-600"
                  data-testid="sms-opt-in-mockup-helper"
                >
                  {t("smsOptIn.mockupHelper")}
                </p>

                {/* SMS consent checkbox — Twilio A2P 10DLC requires
                    explicit opt-in. Renders UNCHECKED by default to
                    mirror the real Settings page behaviour. Non-
                    interactive (readOnly + tabIndex={-1}) so screen
                    readers + keyboard users immediately know this is
                    an illustration. */}
                <label
                  className="mt-4 flex items-start gap-2.5 p-3 rounded-md border border-gray-200 bg-gray-50 cursor-default"
                  data-testid="sms-opt-in-mockup-consent-block"
                >
                  <input
                    type="checkbox"
                    checked={false}
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                    className="mt-0.5 w-4 h-4 shrink-0 accent-green-800"
                    data-testid="sms-opt-in-mockup-consent-checkbox"
                  />
                  <span className="text-[13px] leading-relaxed text-gray-700">
                    {t("smsOptIn.mockupCheckboxLabel")}
                  </span>
                </label>

                <ul className="mt-3 space-y-1.5 text-[13px] text-gray-600">
                  {[
                    t("smsOptIn.mockupBullet1"),
                    t("smsOptIn.mockupBullet2"),
                    t("smsOptIn.mockupBullet3"),
                    t("smsOptIn.mockupBullet4"),
                    t("smsOptIn.mockupBullet5"),
                  ].map((line, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2"
                      data-testid={`sms-opt-in-mockup-bullet-${idx + 1}`}
                    >
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0" aria-hidden="true" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-5 flex items-center gap-3">
                  <span
                    className="inline-flex items-center justify-center h-9 px-4 rounded-md bg-green-800 text-white text-sm font-medium cursor-default select-none opacity-90"
                    aria-hidden="true"
                    data-testid="sms-opt-in-mockup-save-button"
                  >
                    {t("smsOptIn.mockupSaveButton")}
                  </span>
                  <span className="text-xs text-gray-400">{t("smsOptIn.mockupCaption")}</span>
                </div>
              </div>
            </figure>
          </section>

          {/* "How opt-in works" — explicit recap of the exact opt-in
              ceremony (login → settings → phone → checkbox → save) plus
              a 1-line-per-bullet expansion of every key compliance fact
              a Twilio Toll-Free / A2P 10DLC reviewer needs to verify
              without logging in. Mirrors the i18n keys precisely so
              copy edits flow through one place. */}
          <section className="mt-10" data-testid="sms-opt-in-how-works">
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">
              {t("smsOptIn.howOptInWorksTitle")}
            </h2>
            <p className="mt-3 text-gray-600 leading-relaxed" data-testid="sms-opt-in-how-works-intro">
              {t("smsOptIn.howOptInWorksIntro")}
            </p>
            <ul className="mt-4 space-y-2.5 text-gray-600 leading-relaxed">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <li
                  key={n}
                  className="flex items-start gap-2.5"
                  data-testid={`sms-opt-in-how-works-point-${n}`}
                >
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-green-700 shrink-0" />
                  <span>{t(`smsOptIn.howOptInPoint${n}`)}</span>
                </li>
              ))}
            </ul>
          </section>

          <Section title={t("smsOptIn.messageTypesTitle")} testid="sms-opt-in-messages">
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="w-5 h-5 mt-0.5 text-green-700 shrink-0" />
                <span className="text-gray-700">{t("smsOptIn.messageType1")}</span>
              </li>
            </ul>
          </Section>

          <Section title={t("smsOptIn.frequencyTitle")} testid="sms-opt-in-frequency">
            <p>{t("smsOptIn.frequencyBody")}</p>
          </Section>

          <Section title={t("smsOptIn.ratesTitle")} testid="sms-opt-in-rates">
            <p>{t("smsOptIn.ratesBody")}</p>
          </Section>

          <Section title={t("smsOptIn.helpStopTitle")} testid="sms-opt-in-help-stop">
            <p>{t("smsOptIn.helpStopBody")}</p>
          </Section>

          <Section title={t("smsOptIn.privacyTitle")} testid="sms-opt-in-privacy">
            <p>{t("smsOptIn.privacyBody")}</p>
            <p data-testid="sms-opt-in-privacy-addon-1">{t("smsOptIn.privacyAddon1")}</p>
            <p data-testid="sms-opt-in-privacy-addon-2">{t("smsOptIn.privacyAddon2")}</p>
          </Section>

          <div className="mt-10 rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="sms-opt-in-customer-note">
            <h3 className="font-display text-lg font-semibold text-amber-900">
              {t("smsOptIn.customerNoteTitle")}
            </h3>
            <p className="mt-2 text-amber-900/85 leading-relaxed">
              {t("smsOptIn.customerNoteBody")}
            </p>
          </div>

          {/* Related Documents — cross-reference block matching the
              identical block in /privacy and /terms, so a Twilio A2P
              10DLC reviewer can trace all three public documents from
              any entry point. */}
          <Section title={t("smsOptIn.relatedDocsTitle")} testid="sms-opt-in-related-docs">
            <ul className="space-y-2 text-gray-600">
              <li data-testid="sms-opt-in-related-docs-program">
                <span className="font-medium text-gray-900">{t("smsOptIn.relatedDocsProgramLabel")}</span>{" "}
                <a
                  href="https://cleaning.tasklio.co/sms-opt-in"
                  className="text-green-800 hover:underline break-all"
                >
                  https://cleaning.tasklio.co/sms-opt-in
                </a>
              </li>
              <li data-testid="sms-opt-in-related-docs-privacy">
                <span className="font-medium text-gray-900">{t("smsOptIn.relatedDocsPrivacyLabel")}</span>{" "}
                <a
                  href="https://cleaning.tasklio.co/privacy"
                  className="text-green-800 hover:underline break-all"
                >
                  https://cleaning.tasklio.co/privacy
                </a>
              </li>
              <li data-testid="sms-opt-in-related-docs-terms">
                <span className="font-medium text-gray-900">{t("smsOptIn.relatedDocsTermsLabel")}</span>{" "}
                <a
                  href="https://cleaning.tasklio.co/terms"
                  className="text-green-800 hover:underline break-all"
                >
                  https://cleaning.tasklio.co/terms
                </a>
              </li>
            </ul>
          </Section>

          <Section title={t("smsOptIn.contactTitle")} testid="sms-opt-in-contact">
            <p>
              {t("smsOptIn.contactBody")}{" "}
              <a
                href="mailto:support@tasklio.co"
                className="text-green-800 hover:underline font-medium"
                data-testid="sms-opt-in-support-email"
              >
                support@tasklio.co
              </a>
            </p>
          </Section>

          <div className="mt-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-green-800 hover:underline font-medium"
              data-testid="sms-opt-in-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("smsOptIn.backToHome")}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
